import React, { useEffect, useState } from 'react';
import { Alert, Stack, styled, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import palette from '../../theme/palette';
import useLocales from '../../hooks/useLocales';
import useCountdown from '../../hooks/useCountdown';
import CustomModal from '../../components/CustomModal';
import { InputContainer, StyledInput, StyledTextField, ThemeButton } from '../../layouts/GlobalStyles';
import conObj, { showResponseError } from '../../utils/connection-assistant';
import uuidv4 from '../../utils/uuidv4';
import PhonenumberTextField2 from '../../components/PhonenumberTextField2';
import { useSelector } from '../../redux/store';
import useAuth from '../../hooks/useAuth';
import { getUserMemberBalance } from '../../redux/slices/lookup';

const Label = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontFamily: 'sans-serif',
  fontWeight: '600',
  color: palette.visitor.dialog.otherText,
}));

const AccountOTP = ({ open, onClose }) => {
  const { translate } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const { userProfile } = useSelector((x) => x.lookup);

  const [phoneNumber, setPhoneNumber] = useState('');

  const [otpUuid, setOtpUuid] = useState('');

  const [otpCode, setOtpCode] = useState();

  const [resendExpiry, setResendExpiry] = useState(null);

  const resendCountdown = useCountdown(resendExpiry);

  const [errors, setErrors] = useState({
    phoneNumber: '',
    otpCode: '',
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setPhoneNumber(userProfile?.UserProfile?.no_hp);
  }, [userProfile]);

  const validatePhoneNumber = () => {
    if (phoneNumber.length !== 13) {
      setErrors((prev) => ({ ...prev, phoneNumber: translate('minimum_length_is_x', { x: 10 }) }));
      return false;
    }

    return true;
  };

  const requestOtp = async () => {
    try {
      setIsLoading(true);
      setResendExpiry(null);
      let fingerprint = localStorage.getItem('fingerprint');
      if (!fingerprint) {
        fingerprint = uuidv4();
        localStorage.setItem('fingerprint', fingerprint);
      }

      const response = await conObj.post('otp_request.json', {
        no_hp: phoneNumber.replace(/[^0-9]/g, ''),
        fingerprint,
        purpose: 'verifyaccount',
      });

      setOtpUuid(response.data?.data?.data?.otp_uuid);
      setResendExpiry(moment().add(1, 'minute').toISOString());
    } catch (err) {
      console.log(`Error: ${err.message}`);
      const snackbarOptions = { variant: 'error', autoHideDuration: 5000 };
      enqueueSnackbar(err.message, snackbarOptions);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      if (!otpCode) {
        setErrors((prev) => ({ ...prev, otpCode: translate('x_is_required', { x: translate('verification_code') }) }));
        return;
      }

      const response = await conObj.post('verify_account_by_otp.json', {
        otp_uuid: otpUuid,
        otp_code: otpCode,
      });

      getUserMemberBalance();

      enqueueSnackbar(response?.data?.data?.msg);
      onClose();
    } catch (error) {
      console.log(error);
      showResponseError(error, enqueueSnackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    setErrors({
      phoneNumber: '',
      otpCode: '',
    });

    if (otpUuid) onSubmit();
    else {
      const isValid = validatePhoneNumber();
      if (isValid) {
        requestOtp();
      }
    }
  };

  const handleClose = () => {
    onClose();
    setErrors({
      phoneNumber: '',
      otpCode: '',
    });
  };

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      title={translate('phonenumber_verification')}
      isBackdropClosable={false}
    >
      <Stack sx={{ p: 2 }} spacing={1}>
        {otpUuid ? (
          <Alert severity="success" sx={{ mb: 1 }}>
            {translate('otp_sent')}
          </Alert>
        ) : (
          ''
        )}

        <Label fontWeight={600}>
          {!otpUuid ? translate('phone_number') : translate('verification_code')}{' '}
          <span style={{ color: 'red' }}>*</span>
        </Label>

        <InputContainer direction="row">
          {!otpUuid ? (
            <PhonenumberTextField2
              readOnly
              value={phoneNumber}
              placeholder={translate('phone_number')}
              onChange={(e) => setPhoneNumber(e)}
              fullWidth
            />
          ) : (
            <StyledInput
              placeholder={translate('input_otp')}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
            />
          )}
        </InputContainer>

        {otpUuid ? (
          <>
            {resendCountdown.seconds !== '00' ? (
              <Typography sx={{ fontSize: '0.75rem' }}>
                {translate('send_again_in_x', { x: resendCountdown.seconds })}
              </Typography>
            ) : (
              <Typography
                sx={{ fontSize: '0.75rem', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => requestOtp()}
              >
                {translate('send_again')}
              </Typography>
            )}
          </>
        ) : (
          <></>
        )}

        {errors?.phoneNumber || errors?.otpCode ? (
          <Typography sx={{ fontSize: '0.75rem', color: 'error.main' }}>
            {errors?.otpCode || errors?.phoneNumber || ''}
          </Typography>
        ) : (
          ''
        )}

        <ThemeButton loading={isLoading} onClick={handleClick}>
          {translate('submit')}
        </ThemeButton>
      </Stack>
    </CustomModal>
  );
};

export default AccountOTP;
